import { Activity, ShieldCheck, Zap, FileSpreadsheet, Lock, HeartHandshake } from 'lucide-react';
import Container from '../../common/Container';
import ScrollReveal from '../common/ScrollReveal';

const features = [
  {
    icon: Activity,
    title: 'Unified Healthcare Operations',
    description:
      'Connect every department — reception, OPD clinics, IPD wards, doctor consultations, and cash counters — into a single synchronized ecosystem.',
  },
  {
    icon: Zap,
    title: 'Lightning-Fast Front Desk',
    description:
      'Register new patients and bill multiple OPD services in under 60 seconds. Minimize waiting room chaos and elevate patient satisfaction.',
  },
  {
    icon: ShieldCheck,
    title: 'Smart Bed & Census Tracking',
    description:
      'Visual bed management (ATD) with real-time status: vacant, occupied, or reserved. Categorize by ICU, General Ward, Deluxe, and Private suites.',
  },
  {
    icon: FileSpreadsheet,
    title: 'One-Click Exports & PDF Print',
    description:
      'Instantly generate hospital-branded discharge summaries, printable patient receipts, and export detailed financial ledgers to Excel.',
  },
  {
    icon: Lock,
    title: 'Role-Based Access & Security',
    description:
      'Protect sensitive medical data with granular permissions. Separate administrative configurations from clinical and billing workflows.',
  },
  {
    icon: HeartHandshake,
    title: 'Dedicated Enterprise Support',
    description:
      'Backed by Devdeep Systems with direct local support, staff training, and continuous product updates tailored to your hospital needs.',
  },
];

export const Features = () => {
  return (
    <section id="features" className="py-20 md:py-28 bg-slate-900 text-white relative overflow-hidden">
      {/* Background glow effects */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#0D7893]/20 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-teal-500/10 blur-3xl rounded-full pointer-events-none" />

      <Container>
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <ScrollReveal direction="down">
            <span className="inline-flex items-center gap-1 px-3.5 py-1 rounded-full bg-[#0D7893]/20 text-[#38bcd8] text-xs font-bold uppercase tracking-wider border border-[#0D7893]/40">
              Why Choose Divine HMS
            </span>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={100}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
              Engineered for Complete Clinical & Operational Control
            </h2>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={200}>
            <p className="text-slate-400 text-base md:text-lg">
              Designed from the ground up to solve hospital administration pain points — reducing manual errors, accelerating patient throughput, and providing crystal-clear financial visibility.
            </p>
          </ScrollReveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((item, idx) => {
            const Icon = item.icon;
            return (
              <ScrollReveal
                key={idx}
                direction="up"
                delay={idx * 100}
                className="group relative p-8 rounded-3xl bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 hover:border-[#0D7893]/50 transition-all duration-300 shadow-xl hover:shadow-[#0D7893]/10"
              >
                <div className="p-3.5 rounded-2xl bg-[#0D7893]/20 text-[#38bcd8] w-fit mb-6 group-hover:bg-[#0D7893] group-hover:text-white transition-all duration-300">
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {item.description}
                </p>
              </ScrollReveal>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export default Features;
